import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Check, ChevronDown } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import DateRangePicker from "@/components/controls/DateRangePicker";
import { cn } from "@/utils/utils";
import { ensureDate } from "@/utils/dateRange";
import { useFiscalYearStartMonth } from "@/entities/settings/useFiscalYearStartMonth";
import {
  COMPARE_PERIOD_OPTIONS,
  formatPeriodRange,
  formatTimeframeLabel
} from "@/entities/dataTables/compareTo/periods";
import type {
  ComparePeriod,
  CompareToSelection
} from "@/entities/dataTables/compareTo/types";

const TRANSLATION_KEY = "datatoolbar.display.compareTo";

interface Props {
  value: CompareToSelection;
  onChange: (selection: CompareToSelection) => void;
}

interface OptionRowProps {
  label: string;
  hint?: string;
  isSelected: boolean;
  onClick: () => void;
  /** Per-period selector, so a test can address one preset rather than a nth-child. */
  dataTest: string;
}

/**
 * Mirrors DatePresetItem's interaction styling, but the right-hand slot holds
 * either the resolved range or the check — never both, as in the design.
 */
const OptionRow: React.FC<OptionRowProps> = ({
  label,
  hint,
  isSelected,
  onClick,
  dataTest
}) => (
  // A real button rather than a clickable div: the row is keyboard-reachable
  // for free, and `menuitemradio` + `aria-checked` is what tells a screen
  // reader this is a one-of-many choice and which one is active. The check
  // glyph alone conveys that to sighted users only.
  <button
    data-test={dataTest}
    type="button"
    role="menuitemradio"
    aria-checked={isSelected}
    onClick={onClick}
    className={cn(
      "flex w-full items-center justify-between gap-4 px-3 py-2 cursor-pointer rounded-lg text-left",
      "hover:bg-surface-hover-soft active:bg-surface-pressed",
      "transition-all duration-200 ease-in-out"
    )}
  >
    <Text as="span" className="text-nowrap">
      {label}
    </Text>
    {isSelected ? (
      <IconContainer as="span">
        <Check />
      </IconContainer>
    ) : (
      hint && (
        <Text as="span" variant="smBodyTertiary" className="text-nowrap">
          {hint}
        </Text>
      )
    )}
  </button>
);

const CompareToSelect: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const fiscalYearStartMonth = useFiscalYearStartMonth();
  const [isOpen, setIsOpen] = useState(false);
  const [isPickingRange, setIsPickingRange] = useState(false);

  const triggerLabel = formatTimeframeLabel(value, t);

  // Recomputed per open rather than memoized on a clock: the hints are dates,
  // and a session left open across midnight would otherwise show yesterday's.
  const hints = useMemo(() => {
    if (!isOpen) return {} as Partial<Record<ComparePeriod, string>>;
    const today = new Date();
    return COMPARE_PERIOD_OPTIONS.reduce<Partial<Record<ComparePeriod, string>>>(
      (periodHints, period) => {
        if (period !== "custom") {
          periodHints[period] = formatPeriodRange(
            period,
            fiscalYearStartMonth,
            today
          );
        }
        return periodHints;
      },
      {}
    );
  }, [isOpen, fiscalYearStartMonth]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setIsPickingRange(false);
  };

  const handlePresetSelect = (period: ComparePeriod) => {
    if (period === "custom") {
      setIsPickingRange(true);
      return;
    }
    onChange({ period });
    handleOpenChange(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        data-test="compare-to-trigger"
        className={cn(
          "flex w-full justify-between items-center gap-2 h-9 pl-3 pr-2 py-[6px] rounded-lg",
          "border-[0.5px] bg-surface-field-default shadow-field text-left",
          "transition-all duration-200 ease-in-out",
          isOpen
            ? "border-border-field-focus bg-surface-focus-selected shadow-field-focus"
            : "border-border-field-default hover:border-border-field-hover"
        )}
      >
        <Text as="span" variant="body" className="truncate">
          {triggerLabel}
        </Text>
        <IconContainer as="span" color="secondary">
          <ChevronDown />
        </IconContainer>
      </PopoverTrigger>
      <PopoverContent
        data-test="compare-to-options"
        align="start"
        sideOffset={4}
        padding={4}
        className={isPickingRange ? "w-auto" : "w-[20rem]"}
      >
        {isPickingRange ? (
          <DateRangePicker
            labels={{ cancel: t("cancel"), apply: t("apply") }}
            initialFromDate={ensureDate(value.customStart) ?? undefined}
            initialToDate={ensureDate(value.customEnd) ?? undefined}
            alwaysApply
            onCancel={() => setIsPickingRange(false)}
            onUpdate={({ range }) => {
              const fromDate = ensureDate(range.fromDate);
              if (!fromDate) return;
              onChange({
                period: "custom",
                customStart: fromDate,
                customEnd: ensureDate(range.toDate) ?? undefined
              });
              handleOpenChange(false);
            }}
          />
        ) : (
          <div className="flex flex-col" role="menu">
            {COMPARE_PERIOD_OPTIONS.map((period) => (
              <OptionRow
                key={period}
                dataTest={`compare-to-option-${period}`}
                label={t(`${TRANSLATION_KEY}.presets.${period}`)}
                hint={hints[period]}
                isSelected={value.period === period}
                onClick={() => handlePresetSelect(period)}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default CompareToSelect;
